import React, { useState } from 'react';
import { Hash, Copy, Trash2, RefreshCw } from 'lucide-react';

const HashIdGeneratorTool = () => {
  const [length, setLength] = useState(8);
  const [alphabet, setAlphabet] = useState('alphanumeric');
  const [count, setCount] = useState(10);
  const [prefix, setPrefix] = useState('');
  const [ids, setIds] = useState([]);
  const [copied, setCopied] = useState('');

  const alphabets = {
    alphanumeric: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
    lowercase: 'abcdefghijklmnopqrstuvwxyz0123456789',
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
    hex: '0123456789abcdef',
    numbers: '0123456789',
    friendly: 'abcdefghjkmnpqrstuvwxyz23456789', // No confusing chars (0, o, l, 1, i)
    base62: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  };

  const generateId = () => {
    const chars = alphabets[alphabet];
    let id = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    
    for (let i = 0; i < length; i++) {
      id += chars[array[i] % chars.length];
    }
    
    return prefix ? `${prefix}${id}` : id;
  };

  const generateIds = () => {
    const newIds = [];
    for (let i = 0; i < count; i++) {
      newIds.push(generateId());
    }
    setIds(newIds);
  };

  const copyId = (id, index) => {
    navigator.clipboard.writeText(id);
    setCopied(index);
    setTimeout(() => setCopied(''), 1500);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(ids.join('\n'));
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  const calculateEntropy = () => {
    const charsetSize = alphabets[alphabet].length;
    return (Math.log2(charsetSize) * length).toFixed(1);
  };

  const estimateCombinations = () => {
    const charsetSize = alphabets[alphabet].length;
    const combinations = Math.pow(charsetSize, length);
    if (combinations >= 1e15) return `${(combinations / 1e15).toFixed(1)} quadrillion`;
    if (combinations >= 1e12) return `${(combinations / 1e12).toFixed(1)} trillion`;
    if (combinations >= 1e9) return `${(combinations / 1e9).toFixed(1)} billion`;
    if (combinations >= 1e6) return `${(combinations / 1e6).toFixed(1)} million`;
    return combinations.toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Length</label>
          <input
            type="number"
            value={length}
            onChange={(e) => setLength(Math.max(4, Math.min(32, parseInt(e.target.value) || 8)))}
            min="4"
            max="32"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Character Set</label>
          <select
            value={alphabet}
            onChange={(e) => setAlphabet(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="alphanumeric">Alphanumeric (a-Z, 0-9)</option>
            <option value="lowercase">Lowercase (a-z, 0-9)</option>
            <option value="uppercase">Uppercase (A-Z, 0-9)</option>
            <option value="hex">Hexadecimal (0-f)</option>
            <option value="numbers">Numbers only (0-9)</option>
            <option value="friendly">URL-friendly (no confusing chars)</option>
            <option value="base62">Base62</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Count</label>
          <input
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
            min="1"
            max="100"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Prefix (optional)</label>
          <input
            type="text"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="e.g., user_, id-"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">ID Strength</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Entropy:</span>
            <span className="text-blue-400 ml-2">{calculateEntropy()} bits</span>
          </div>
          <div>
            <span className="text-gray-500">Combinations:</span>
            <span className="text-green-400 ml-2">{estimateCombinations()}</span>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={generateIds}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <Hash size={18} />
          Generate IDs
        </button>
        <button
          onClick={generateIds}
          disabled={ids.length === 0}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {ids.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-300">Generated IDs</label>
            <button
              onClick={copyAll}
              className={`text-xs px-3 py-1 rounded transition-colors ${
                copied === 'all' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {copied === 'all' ? 'Copied!' : 'Copy All'}
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {ids.map((id, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <code className="font-mono text-green-400 text-sm truncate mr-2">{id}</code>
                <button
                  onClick={() => copyId(id, i)}
                  className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                    copied === i ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setIds([])}
        disabled={ids.length === 0}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default HashIdGeneratorTool;
